<template>
  <div>
    <template>
      <ValidationObserver
        ref="observer"
        v-slot="{ valid, dirty }"
      >
        <NavTabs
          customClass="capitalize_title"
          :tabs="tabs"
          :activeTab="newActiveTab"
          :sample="sample"
          @sendData="updateData"
          @clients="mutableValue.clients = $event"
          @sites="mutableValue.sites = $event"
          @tree="mutableValue.tree = $event"
          @updateData="updateSampleData"
          @inputChange="(data) => $emit('inputChange', data)"
        />

        <SlotScopeWatcher
          :value="dirty"
          @change="$emit('dirtyChanged', dirty)"
        />
        <SlotScopeWatcher
          :value="valid"
          @change="$emit('validChanged', valid)"
        />
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import TypesForm from '@/components/forms/sample/TypesForm';
import DescriptionForm from '@/components/forms/sample/DescriptionForm';
import AnalysisTypesForm from '@/components/forms/sample/AnalysisTypesForm';
import SummaryForm from '@/components/forms/sample/SummaryForm';
import analysisTypesConstant from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import sampleTypes from '@/constants/sampleTypes';
import NavTabs from '@/components/shared/widgets/Nav-tabs.vue';
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';

export default {
    components: {
        NavTabs,
        SlotScopeWatcher,
        ValidationObserver,
    },
    props: {
        isWorkOrder: {
            type: Boolean,
            default: false,
        },
        isLab: {
            type: Boolean,
            default: false,
        },
        isTemplate: {
            type: Boolean,
            default: false,
        },
        isSite: {
            type: Boolean,
            default: false,
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
        sample: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            components: [TypesForm, DescriptionForm, AnalysisTypesForm, SummaryForm],
            tabs: [],
            summaryStep: 4,
            mutableValue: null,
            initParameters: null,
            empty: {
                loading: false,
                // TYPES
                sample: {
                    client: null,
                    site: null,
                    component: null,
                    type: null,
                    // DESCRIPTION
                    samples: [],
                    note: undefined,
                    // Water
                    treatment: undefined,
                    otherTreatment: undefined,
                    // Glycol
                    glycolType: undefined,
                    glycolOther: undefined,
                    // Legionellosis
                    biocideInfo: undefined,
                    sampleDate: undefined,
                    temperature: undefined,
                    temperatureUnit: undefined,
                    sampledBy: undefined,
                    // Deposit
                    estimatedDepositAge: undefined,
                    application: undefined,
                    materialType: undefined,
                    waterType: undefined,
                    incidents: undefined,
                    // Contenance
                    tracerAddedDate: undefined,
                    collectionLossDate: undefined,
                    quantityAdded: undefined,
                    estimatedVolume: undefined,
                    addedLithium: undefined,
                    addedTracer: undefined,
                    evaluation: undefined,
                    // Coupon
                    applicableNorms: undefined,
                    // Resin
                    makerName: undefined,
                    resinNumber: undefined,
                    resinType: undefined,
                    age: undefined,
                    analysisReason: undefined,
                    // Other
                    AROtherAdditionalInfo: undefined,
                    // ANALYSIS TYPES
                    analysisTypes: [],
                    billable: false,
                    poNumber: '',
                    template: undefined,
                    selectedAnalysisGroups: [],
                    selectedContracts: [],
                },
            },
        };
    },
    computed: {
        ...mapGetters([
            'activeTab',
            'userLang',
            'getSteps',
            'getSample',
            'teamMainLang',
        ]),
        analysisTypes() {
            if (this.mutableValue.sample.type === sampleTypes.LEGIONELLOSIS) {
                return this.mutableValue.analysisTypesByLegionella;
            }
            return this.mutableValue.analysisTypes;
        },
        sampleTypes() {
            return sampleTypes;
        },
        newActiveTab: {
            get() {
                return this.activeTab;
            },
            set(newValue) {
                this.$store.dispatch('changeActiveTab', newValue);
            },
        },
    },
    watch: {
        'mutableValue.sample.type': {
            handler() {
                const stepsCopy = cloneDeep(this.getSteps);
                if (this.mutableValue.sample.type === 'DepositSample') {
                    const indexOfStep = this.getSteps.findIndex(step => step.title === this.$t('analysisRequests.labels.analysisTypes'));
                    if (indexOfStep !== -1) {
                        stepsCopy.splice(indexOfStep, 1);
                        this.$store.dispatch('setSteps', stepsCopy);
                    }
                    this.summaryStep = 3;
                } else {
                    const indexOfStep = this.getSteps.findIndex(step => step.title === this.$t('analysisRequests.labels.analysisTypes'));
                    if (indexOfStep === -1) {
                        stepsCopy.splice(2, 0, { completed: false, title: this.$t('analysisRequests.labels.analysisTypes') });
                        this.$store.dispatch('setSteps', stepsCopy);
                    }
                    this.summaryStep = 4;
                }
            },
        },
    },
    /**
     * As this form has multiple concurrent instances in the wizard modal,
     * the data are loaded only once (in the first step)
     * The wizard modal will take care of noticing the other instances of the state change.
     * This will avoid multiplying the amount of requests made by the amount of total steps/instances in the wizard.
     */
    async created() {
        this.mutableValue = { ...this.empty };
        this.$store.dispatch('changeActiveTab', 0);
        if (this.sample?.client) {
            if (this.isWorkOrder && !this.isEdition) {
                this.mutableValue.sample.client = this.sample.client;
                this.mutableValue.sample.site = this.sample.site;
                this.mutableValue.sample.component = this.sample.component;
            } else if (this.isEdition) {
                this.mutableValue = { sample: this.sample };
            } else if (this.isLab) {
                this.mutableValue = { sample: this.sample };
            }
        }

        this.mutableValue.loading = true;
        const { data: analysisGroups } = await this.$rq.getAnalysisGroups();
        const { data: { types: allTypes } } = await this.$rq.getAnalysisTypes({ pagination: false, typeName: 'All' });
        const analysisTypes = allTypes.filter(t => t.type === analysisTypesConstant.SIMPLE_ANALYSIS_TYPE
            || t.type === analysisTypesConstant.CALCULATED_ANALYSIS_TYPE
            || t.type === analysisTypesConstant.TYPE_TEXT || t.type === analysisTypesConstant.TYPE_LIST);

        // For now we use the same for Legionella culture
        const { data: { types: analysisTypesByLegionella } } = await this.$rq.getAnalysisTypes(
            { typeName: analysisTypesConstant.TYPE_LEGIONELLA, pagination: false },
        );
        const { data: { coupons } } = await this.$rq.getCoupons({ pagination: false });
        await this.getParamsWithInitParameters();
        this.mutableValue.analysisGroups = analysisGroups.groups.map(({ id, name, types, ERPCode }) => ({
            id,
            name,
            types: types.map(type => type.key),
            ERPCode,
        }));
        this.mutableValue.analysisTypes = analysisTypes;
        this.mutableValue.analysisTypesByLegionella = analysisTypesByLegionella;
        this.mutableValue.coupons = coupons.map(type => ({
            id: type.id,
            name: type.name[this.userLang] || type.name[this.teamMainLang],
            density: type.density,
            surface: type.surface,
        }));
        this.mutableValue.loading = false;
        this.mutableValue.billable = !!this.sample.billable;
        this.mutableValue.poNumber = this.sample.poNumber;
        this.mutableValue.isLab = this.isLab;
        this.mutableValue.isTemplate = this.isTemplate;
        if (this.isTemplate) {
            // set client and site
            this.mutableValue.sample.site = this.sample.site;
            this.mutableValue.sample.client = this.sample.client;
        }
        this.$store.dispatch('setSample', { ...this.mutableValue.sample });
        this.updateData();
        this.tabs = this.tableHeaders();
    },
    methods: {
        tableHeaders() {
            const payload = {
                mutableValue: this.mutableValue,
                isWorkOrder: this.isWorkOrder,
                isLab: this.isLab,
                isEdition: this.isEdition,
                isTemplate: this.isTemplate,
                empty: this.empty,
                isSite: this.isSite,
            };
            const headerTitle = ['types', 'description', 'analysisTypes', 'summary'];
            const headers = this.components.map((component, index) => ({
                index,
                props: {
                    ...payload,
                    selectedGroups: headerTitle[index] === 'analysisTypes' ? this.sample.selectedAnalysisGroups : [],
                },
                name: this.$t(`analysisRequests.labels.${headerTitle[index]}`),
                component: Vue.component(`tab-${index}`, component),
                disabled: true,
            }));
            this.$store.dispatch('setTabs', headers);
            return headers;
        },
        async getParamsWithInitParameters() {
            if (!this.sample) {
                return;
            }
            const {
                client,
                site,
                component,
                analysisTypes,
                samples,
            } = this.sample;

            if (client) {
                const { data } = await this.$rq.getClientInfo(client);
                this.mutableValue.client = [data];
            }

            if (site) {
                const { data } = await this.$rq.getSiteInfo(site);
                this.mutableValue.site = [data];
            }

            if (component) {
                const node = await this.$rq.getNode(component, 'component');
                if (node) {
                    this.mutableValue.tree = [node.data];
                }
            }


            if (analysisTypes) {
                this.mutableValue.sample.analysisTypes = analysisTypes.map(({ key }) => key);
            }

            if (samples) {
                this.mutableValue.sample.samples = samples.map(sample => ({
                    ...sample,
                    identification: sample.identification,
                    comment: sample.comment,
                    collectedDate: sample.collectedDate,
                }));
            }
        },
        updateSampleData(e) {
            this.$store.dispatch('updateSample', { ...this.getSample, ...e });
            this.updateData();
        },
        updateData() {
            this.$emit('sendData', this.getSample);
        },
    },
};
</script>
